import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import OrganizationsMultiSelect from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect';
import PropertiesMultiSelect from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect';
import type { Organization } from '@/hooks/useOrganizations';
import type { Property } from '@/types/property';

interface CalendarFiltersBarProps {
  organizations: Organization[];
  properties: Property[];
  selectedOrganizations: string[];
  selectedProperties: string[];
  onOrganizationsChange: (ids: string[]) => void;
  onPropertiesChange: (ids: string[]) => void;
  visibleMonth?: string;
  monthOptions: { value: string; label: string }[];
  onMonthSelect: (value: string) => void;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
}

const CalendarFiltersBar = ({
  organizations,
  properties,
  selectedOrganizations,
  selectedProperties,
  onOrganizationsChange,
  onPropertiesChange,
  visibleMonth,
  monthOptions,
  onMonthSelect,
  onPrevious,
  onToday,
  onNext,
}: CalendarFiltersBarProps) => {
  return (
    <div className="flex items-center justify-between flex-wrap gap-4">
      {/* Left side: Organizations and Properties filters */}
      <div className="flex items-center gap-3">
        <OrganizationsMultiSelect
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onSelectionChange={onOrganizationsChange}
        />
        <PropertiesMultiSelect
          properties={properties}
          selectedProperties={selectedProperties}
          onSelectionChange={onPropertiesChange}
        />
      </div>

      {/* Right side: Month selector and navigation */}
      <div className="flex items-center gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="justify-between min-w-[160px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
              <span className="truncate">{visibleMonth || 'Seleccionar mes'}</span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-2" align="end">
            <div className="space-y-1">
              {monthOptions.map((option) => (
                <Button
                  key={option.value}
                  variant="ghost"
                  className="w-full justify-start text-sm"
                  onClick={() => onMonthSelect(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Button variant="ghost" size="sm" className="rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="rounded-full px-4 bg-gray-100 hover:bg-gray-200 transition-colors" onClick={onToday}>
          Hoy
        </Button>
        <Button variant="ghost" size="sm" className="rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" onClick={onNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default CalendarFiltersBar;
